import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { LOCAL_FUNCTIONS, TRACKING_HEADERS } from '@/integrations/supabase/growthHubClient';

const getVisitorId = () => localStorage.getItem('obc_visitor_id') || '';
const getSessionId = () => sessionStorage.getItem('obc_session_id') || '';

/**
 * Sends a final engagement beacon (time_on_page + max scroll depth)
 * when the visitor navigates away or the tab becomes hidden.
 * Uses navigator.sendBeacon for reliability on page unload.
 */
export const useEngagementBeacon = () => {
  const location = useLocation();
  const pageEnteredAt = useRef(Date.now());
  const maxScrollDepth = useRef(0);
  const hasSentBeacon = useRef(false);

  // Track max scroll depth via scroll listener
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight > 0) {
        const depth = Math.min(100, Math.round((scrollTop / docHeight) * 100));
        if (depth > maxScrollDepth.current) {
          maxScrollDepth.current = depth;
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  // Send beacon on visibility change or beforeunload
  useEffect(() => {
    pageEnteredAt.current = Date.now();
    maxScrollDepth.current = 0;
    hasSentBeacon.current = false;

    const sendBeacon = () => {
      if (hasSentBeacon.current) return;
      hasSentBeacon.current = true;

      const timeOnPage = Math.round((Date.now() - pageEnteredAt.current) / 1000);
      // Skip if under 1 second (bot / prefetch)
      if (timeOnPage < 1) return;

      const payload = JSON.stringify({
        action: 'track_event',
        data: {
          session_id: getSessionId(),
          visitor_id: getVisitorId(),
          event_type: 'engagement',
          event_name: 'page_engagement',
          page_url: window.location.href,
          page_path: location.pathname,
          page_title: document.title,
          time_on_page: timeOnPage,
          scroll_depth: maxScrollDepth.current,
        },
      });

      // Prefer sendBeacon (works during unload), fall back to fetch
      if (navigator.sendBeacon) {
        navigator.sendBeacon(LOCAL_FUNCTIONS.visitorTracking, new Blob([payload], { type: 'application/json' }));
      } else {
        fetch(LOCAL_FUNCTIONS.visitorTracking, {
          method: 'POST',
          headers: TRACKING_HEADERS,
          body: payload,
          keepalive: true,
        }).catch(() => {});
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) sendBeacon();
    };

    const handleBeforeUnload = () => sendBeacon();

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      // Send beacon on SPA navigation too
      sendBeacon();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [location.pathname]);
};

export default useEngagementBeacon;
